import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAPICall } from '../../../hooks';
import { Button, ButtonSize, ButtonStyles, ButtonTypes, Theme, Modal, useBreakpoint } from '../../../ui/components';
import ParticipantService from '../../../sdk/services/participantService';
import JoinTeamImage from '../../../assets/image/operationCard/Join_Team.png';
import InvalidJoinTeamImage from '../../../assets/image/operationCard/Invalid_Join_Team.png';
import { DynamicObjectInterface } from '../../../sdk/type';
import { getTranslatedMessage, PAGES, trackPage } from '../../../ui/utils';
import styles from '../Dashboard.module.scss';

interface DashboardAltProps {
  decodedData: DynamicObjectInterface;
}

export const DashboardAlt = (props: DashboardAltProps) => {
  //const { decodedData } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const [showModal, setShowModal] = useState(true);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [invalidModalMessage, setShowInvalidModalMessage] = useState('');
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('tmI');
  const campaignRecId = searchParams.get('cmI');
  const source = searchParams.get('source');
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;

  useEffect(() => {
    const timeout = setTimeout(() => {
      trackPage(PAGES.joinTeam);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (source === 'createTeam') {
      setShowModal(false);
      joinTeam();
    }
  }, [source]);

  const handleClose = () => {
    setShowModal(false);
    navigate('/');
  };

  const joinTeam = async () => {
    let validToJoin = await apiCall(ParticipantService.validateJoinTeam, {
      campaignRecId: campaignRecId,
      teamId: teamId,
    });
    if (validToJoin?.data?.data?.canEnrollParticipant) {
      navigate(
        `/join-team/register?tmI=${teamId}&cmI=${campaignRecId}&tmL=${validToJoin?.data?.data?.canEnrollTeamLeader ? 1 : 0}`,
      );
    } else {
      setShowInvalidModal(true);
      setShowInvalidModalMessage(getTranslatedMessage(t, validToJoin?.data?.message));
      setShowModal(false);
    }
  };

  const joinTeamModal = (
    <>
      {showModal && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={true}
          onClose={handleClose}
          containerWidth={isDesktopView ? '60%' : '80%'}
          primaryButton={
            <Button
              title={t('dashboard.join-team.modal.button.joinTeam')}
              type={ButtonTypes.BUTTON}
              onClick={joinTeam}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.GREEN}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={undefined}
        >
          <div>
            <div>
              <img src={JoinTeamImage} alt={`joinTeamImage`} height={isDesktopView ? '400px' : '200px'} />
            </div>
            <div className={styles['sub-header']}>{t('dashboard.join-team.modal.label.youAreAlmostThere')}</div>
            <div>{t('dashboard.join-team.modal.label.confirmTeamParticipation')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const invalidJoinModal = (
    <>
      {showInvalidModal && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          onClose={() => {
            setShowInvalidModal(false);
            navigate('/');
          }}
          containerWidth={isDesktopView ? '60%' : '80%'}
          primaryButton={
            <Button
              title={t('dashboard.join-team.modal.button.gotIt')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                navigate('/');
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.GREEN}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={undefined}
        >
          <div>
            <div>
              <img src={InvalidJoinTeamImage} alt={`invalidJoinTeamImage`} height={isDesktopView ? '400px' : '200px'} />
            </div>
            <div className={styles['error-text-container']}>
              <div className={styles['sub-header-invalid']}>
                {t('dashboard.join-team.modal.label.invalidJoinTeamTitle')}
              </div>
              <div className={styles['invalid-text']}>
                {t('dashboard.join-team.modal.label.invalidJoinTeamDesc').replace('{api-message}', invalidModalMessage)}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  return (
    <>
      <div className={styles['dashboard-container']}></div>
      {joinTeamModal}
      {invalidJoinModal}
    </>
  );
};
